import { useState, useRef, useEffect } from 'react';
import { Icon } from '../components/Icons';
import { uploadToCloudinary } from '../lib/cloudinary';
import Overview from '../pages/Overview';
import Portfolio from '../pages/Portfolio';
import { About, Metrics, Skills, Systems, Awards, Timeline } from '../pages/Pages';

// Which live page each editor tab previews, fed with the in-progress draft content.
const PREVIEW_FOR = {
  profile: About, overview: Overview, stats: Overview, services: Overview,
  portfolio: Portfolio, skills: Skills, systems: Systems,
  awards: Awards, timeline: Timeline, metrics: Metrics,
};

// Last-5 save snapshots, kept in the browser so you can restore a recent version.
const HISTORY_KEY = 'mr_save_history_v1';
const readHistory = () => {
  try { return JSON.parse(window.localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
};

export default function Admin({ content, onSave, onVerify }) {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [localContent, setLocalContent] = useState(content);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [history, setHistory] = useState(readHistory);

  // Login is validated server-side by the Netlify function (no password in the bundle).
  const handleLogin = async () => {
    setVerifying(true);
    setPwError('');
    const result = await onVerify(pw);
    setVerifying(false);
    if (result.ok) { setAuthed(true); setLocalContent(content); }
    else setPwError(result.error || 'Incorrect password. Try again.');
  };

  if (!authed) return <LoginScreen pw={pw} setPw={setPw} error={pwError} verifying={verifying} onSubmit={handleLogin} />;

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    const result = await onSave(localContent, pw);
    setSaving(false);
    if (result.success) {
      try {
        const snap = { ts: Date.now(), content: JSON.parse(JSON.stringify(localContent)) };
        const next = [snap, ...history].slice(0, 5);
        window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
        setHistory(next);
      } catch { /* localStorage unavailable — skip history */ }
    }
    setSaveMsg(result.success
      ? result.devMode ? 'Saved locally (dev mode)' : 'Saved! Site will update in ~30 seconds.'
      : `Error: ${result.error}`
    );
    setTimeout(() => setSaveMsg(''), 5000);
  };

  const update = (path, value) => {
    const keys = path.split('.');
    setLocalContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const PreviewComp = PREVIEW_FOR[activeTab] || Overview;

  const TABS = [
    { id: 'profile', icon: 'person', label: 'Profile' },
    { id: 'overview', icon: 'home', label: 'Overview' },
    { id: 'stats', icon: 'chart', label: 'Stats & Pills' },
    { id: 'portfolio', icon: 'play', label: 'Portfolio' },
    { id: 'skills', icon: 'bulb', label: 'Skills' },
    { id: 'systems', icon: 'gear', label: 'Systems' },
    { id: 'services', icon: 'star', label: 'Services' },
    { id: 'awards', icon: 'trophy', label: 'Awards' },
    { id: 'timeline', icon: 'calendar', label: 'Timeline' },
    { id: 'metrics', icon: 'chart', label: 'Metrics' },
    { id: 'agentlog', icon: 'bulb', label: 'Agent Log' },
    { id: 'agent', icon: 'mic', label: 'Agent Settings' },
    { id: 'history', icon: 'clock', label: 'Save History' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--cream)' }}>
      {/* Admin sidebar */}
      <div style={{ width: 220, background: 'var(--ink)', color: 'white', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 100 }}>
        <div style={{ padding: '24px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, fontStyle: 'italic', color: 'var(--rose)', marginBottom: 2 }}>Admin Panel</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.08em' }}>makayla-portfolio</div>
        </div>
        <nav style={{ flex: 1, padding: '12px 12px 0' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 700, textAlign: 'left', marginBottom: 2, transition: 'all .15s', background: activeTab === tab.id ? 'rgba(247,207,214,.15)' : 'none', color: activeTab === tab.id ? 'var(--rose)' : 'rgba(255,255,255,.6)' }}>
              <Icon name={tab.icon} size={15} stroke="currentColor" />
              {tab.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(255,255,255,.5)', textDecoration: 'none', marginBottom: 12 }}>
            <Icon name="eye" size={13} stroke="currentColor" /> View Site
          </a>
          <button onClick={() => setShowPreview(p => !p)}
            style={{ width: '100%', padding: '9px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,.22)', background: showPreview ? 'rgba(247,207,214,.18)' : 'transparent', color: showPreview ? 'var(--rose)' : 'rgba(255,255,255,.8)', fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 10 }}>
            <Icon name="eye" size={14} stroke="currentColor" /> {showPreview ? 'Hide Preview' : 'Live Preview'}
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ width: '100%', padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--deep-rose)', color: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 800, cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Icon name="save" size={14} stroke="white" />
            {saving ? 'Saving...' : 'Save & Deploy'}
          </button>
          {saveMsg && <div style={{ marginTop: 8, fontSize: 11, color: saveMsg.includes('Error') ? '#ff6b6b' : 'var(--mint)', textAlign: 'center', lineHeight: 1.4 }}>{saveMsg}</div>}
        </div>
      </div>

      {/* Main admin content */}
      <div style={{ marginLeft: 220, flex: 1, padding: '32px 36px', maxWidth: 900 }}>
        {activeTab === 'profile' && <ProfileEditor data={localContent?.profile} update={update} setLocalContent={setLocalContent} />}
        {activeTab === 'overview' && <OverviewEditor data={localContent} setLocalContent={setLocalContent} />}
        {activeTab === 'stats' && <StatsEditor data={localContent} update={update} setLocalContent={setLocalContent} />}
        {activeTab === 'portfolio' && <PortfolioEditor data={localContent?.portfolio} setLocalContent={setLocalContent} />}
        {activeTab === 'skills' && <SkillsEditor data={localContent?.skills} setLocalContent={setLocalContent} />}
        {activeTab === 'systems' && <SystemsEditor data={localContent?.systems} setLocalContent={setLocalContent} />}
        {activeTab === 'services' && <ServicesEditor data={localContent?.services} setLocalContent={setLocalContent} />}
        {activeTab === 'awards' && <AwardsEditor data={localContent?.awards} setLocalContent={setLocalContent} />}
        {activeTab === 'timeline' && <TimelineEditor data={localContent?.timeline} setLocalContent={setLocalContent} />}
        {activeTab === 'metrics' && <MetricsEditor data={localContent?.metrics} setLocalContent={setLocalContent} />}
        {activeTab === 'agentlog' && <AgentLogEditor pw={pw} />}
        {activeTab === 'agent' && <AgentSettingsEditor data={localContent} update={update} />}
        {activeTab === 'history' && (
          <div>
            <SectionHeader title="Save History" sub="// Your last 5 Save & Deploys (kept in this browser). Restore loads one into the editor — then Save & Deploy to publish it." />
            {history.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace" }}>No saves recorded in this browser yet. Your next Save &amp; Deploy will show up here.</div>
            )}
            {history.map((h, i) => (
              <Card key={h.ts}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>{i === 0 ? 'Most recent save' : `${i} save${i > 1 ? 's' : ''} before that`}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", marginTop: 2 }}>
                      {new Date(h.ts).toLocaleString()} · {(h.content?.portfolio?.length) || 0} portfolio items · {(h.content?.systems?.length) || 0} systems
                    </div>
                  </div>
                  <button onClick={() => {
                    if (window.confirm('Load this saved version into the editor? Your current unsaved edits will be replaced. Nothing goes live until you click Save & Deploy.')) {
                      setLocalContent(JSON.parse(JSON.stringify(h.content)));
                      setActiveTab('profile');
                      setSaveMsg('Restored into editor — review it, then Save & Deploy to publish.');
                      setTimeout(() => setSaveMsg(''), 8000);
                    }
                  }} style={{ padding: '8px 18px', borderRadius: 10, border: '1.5px solid var(--deep-rose)', background: 'white', color: 'var(--deep-rose)', fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>Restore</button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showPreview && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(48vw, 760px)', background: 'var(--cream)', borderLeft: '1px solid var(--border)', boxShadow: '-10px 0 36px rgba(0,0,0,.14)', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', background: 'var(--ink)', color: 'white', flexShrink: 0 }}>
            <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11.5, letterSpacing: '.05em' }}>● LIVE PREVIEW — {activeTab} · updates as you edit, not saved yet</span>
            <button onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '18px 20px' }}>
            {PREVIEW_FOR[activeTab]
              ? <div style={{ zoom: 0.64 }}><PreviewComp content={localContent} /></div>
              : <div style={{ fontSize: 13, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", padding: 20 }}>No visual preview for this tab.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── LOGIN ── */
function LoginScreen({ pw, setPw, error, verifying, onSubmit }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--warm)', borderRadius: 20, padding: 40, width: 360, boxShadow: 'var(--shadow-hover)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 900, fontStyle: 'italic', color: 'var(--ink)', marginBottom: 6 }}>Admin <span style={{ color: 'var(--deep-rose)' }}>Panel</span></div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Enter your password to continue</div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <input type="password" value={pw} onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !verifying && onSubmit()}
            placeholder="Password"
            disabled={verifying}
            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1.5px solid ${error ? 'var(--crimson)' : 'var(--border2)'}`, background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 14, color: 'var(--text)', outline: 'none' }}
          />
        </div>
        {error && <div style={{ fontSize: 12, color: 'var(--crimson)', marginBottom: 8 }}>{error}</div>}
        <button onClick={onSubmit} disabled={verifying}
          style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'var(--crimson)', color: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 800, cursor: verifying ? 'wait' : 'pointer', opacity: verifying ? 0.7 : 1 }}>
          <Icon name="lock" size={14} stroke="white" /> {verifying ? 'Checking…' : 'Enter'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: 'var(--text3)' }}>
          Password is set in Netlify (<code style={{ fontFamily: 'monospace', background: 'var(--blush)', padding: '1px 5px', borderRadius: 4, color: 'var(--crimson)' }}>ADMIN_PASSWORD</code>). Default before setup: <code style={{ fontFamily: 'monospace', background: 'var(--blush)', padding: '1px 5px', borderRadius: 4, color: 'var(--crimson)' }}>admin</code>
        </div>
      </div>
    </div>
  );
}

/* ── SHARED COMPONENTS ── */
const Field = ({ label, children, hint, style }) => (
  <div style={{ marginBottom: 20, ...style }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{hint}</div>}
  </div>
);

const Input = ({ value, onChange, placeholder, type = 'text' }) => (
  <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13, color: 'var(--text)', outline: 'none', transition: 'border .15s' }}
    onFocus={e => e.target.style.borderColor = 'var(--deep-rose)'}
    onBlur={e => e.target.style.borderColor = 'var(--border2)'}
  />
);

const Textarea = ({ value, onChange, placeholder, rows = 4 }) => (
  <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
    style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13, color: 'var(--text)', outline: 'none', resize: 'vertical', transition: 'border .15s' }}
    onFocus={e => e.target.style.borderColor = 'var(--deep-rose)'}
    onBlur={e => e.target.style.borderColor = 'var(--border2)'}
  />
);

const SectionHeader = ({ title, sub }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, fontStyle: 'italic', color: 'var(--ink)', marginBottom: 4 }}>{title}</div>
    {sub && <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace" }}>{sub}</div>}
  </div>
);

const Card = ({ children }) => (
  <div style={{ background: 'white', borderRadius: 14, border: '1.5px solid var(--border)', padding: 20, marginBottom: 16, boxShadow: 'var(--shadow)' }}>
    {children}
  </div>
);

// Collapsible row: shows a title + meta + delete; expands the editor body on click.
// Keeps long lists (Portfolio, Systems, etc.) compact so you don't scroll forever.
const CollapsibleCard = ({ title, meta, accent, onDelete, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1.5px solid var(--border)', marginBottom: 8, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ width: 0, height: 0, borderLeft: '6px solid var(--text3)', borderTop: '5px solid transparent', borderBottom: '5px solid transparent', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }} />
        {accent && <span style={{ width: 9, height: 9, borderRadius: '50%', background: `var(--${accent})`, flexShrink: 0 }} />}
        <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{title || 'Untitled'}</span>
        {meta && <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", flexShrink: 0, whiteSpace: 'nowrap' }}>{meta}</span>}
        {onDelete && <span onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}><DeleteBtn onClick={onDelete} /></span>}
      </div>
      {open && <div style={{ padding: '8px 16px 18px', borderTop: '1px solid var(--border)' }}>{children}</div>}
    </div>
  );
};

const DeleteBtn = ({ onClick }) => (
  <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4, borderRadius: 6, transition: 'all .15s' }}
    onMouseEnter={e => { e.currentTarget.style.color = 'var(--crimson)'; e.currentTarget.style.background = 'var(--blush)'; }}
    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.background = 'none'; }}>
    <Icon name="trash" size={14} stroke="currentColor" />
  </button>
);

const AddBtn = ({ onClick, label }) => (
  <button onClick={onClick}
    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: '1.5px dashed var(--border2)', background: 'var(--warm)', color: 'var(--text2)', fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%', justifyContent: 'center', transition: 'all .15s' }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--rose)'; e.currentTarget.style.color = 'var(--crimson)'; e.currentTarget.style.background = 'var(--blush)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'var(--warm)'; }}>
    <Icon name="plus" size={16} stroke="currentColor" /> {label}
  </button>
);

/* ── CLOUDINARY IMAGE UPLOADER ── */
function ImageUploader({ value, onChange, label = 'Photo' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handleFile = async (file) => {
    setUploading(true);
    setError('');
    const result = await uploadToCloudinary(file);
    setUploading(false);
    if (result.success) {
      onChange(result.url);
      if (result.devMode) setError('Dev mode: using local URL. Set up Cloudinary for permanent hosting.');
    } else {
      setError(result.error || 'Upload failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {value && (
          <img src={value} alt="preview" style={{ width: 80, height: 80, borderRadius: 10, objectFit: 'cover', border: '1.5px solid var(--border)' }} />
        )}
        <div style={{ flex: 1 }}>
          <Input value={value} onChange={onChange} placeholder="Paste image URL or upload below" />
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button onClick={() => inputRef.current?.click()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text)', fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 700, cursor: uploading ? 'wait' : 'pointer' }}>
              <Icon name="upload" size={13} stroke="var(--deep-rose)" />
              {uploading ? 'Uploading...' : `Upload ${label}`}
            </button>
            <input ref={inputRef} type="file" accept="image/*,video/*" style={{ display: 'none' }}
              onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
          </div>
          {error && <div style={{ fontSize: 11, color: 'var(--crimson)', marginTop: 4 }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

/* ── PHOTO GALLERY EDITOR ── */
function PhotoGalleryEditor({ photos, onChange, photoMeta = [], onChangeMeta }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handleFile = async (file) => {
    setUploading(true); setError('');
    const result = await uploadToCloudinary(file);
    setUploading(false);
    if (result.success) { onChange([...photos, result.url]); }
    else setError(result.error || 'Upload failed');
  };

  const handleUrl = () => {
    // eslint-disable-next-line no-restricted-globals
    const url = prompt('Paste image URL:');
    if (url?.trim()) onChange([...photos, url.trim()]);
  };

  const updateMeta = (idx, key, val) => {
    const next = [...(photoMeta || [])];
    while (next.length <= idx) next.push({ zoom: 1, x: 50, y: 0 });
    next[idx] = { ...next[idx], [key]: val };
    onChangeMeta?.(next);
  };

  const removeMeta = (idx) => {
    onChangeMeta?.((photoMeta || []).filter((_, j) => j !== idx));
  };

  return (
    <div>
      {photos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {photos.map((url, i) => {
            const meta = photoMeta[i] || { zoom: 1, x: 50, y: 0 };
            const SLIDERS = [
              { key: 'zoom', label: 'Zoom', min: 1, max: 2, step: 0.05, fmt: v => `${Math.round(v * 100)}%` },
              { key: 'x', label: '← Left / Right →', min: 0, max: 100, step: 1, fmt: v => `${v}%` },
              { key: 'y', label: '↑ Top / Bottom ↓', min: 0, max: 100, step: 1, fmt: v => `${v}%` },
            ];
            return (
              <div key={i} style={{ background: 'var(--warm)', borderRadius: 10, padding: 12, border: '1.5px solid var(--border)' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {/* Live preview */}
                  <div style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', flexShrink: 0, border: '1.5px solid var(--border)', position: 'relative' }}>
                    <img src={url} alt={`slide ${i + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${meta.x ?? 50}% ${meta.y ?? 0}%`, transform: `scale(${meta.zoom ?? 1})`, transformOrigin: `${meta.x ?? 50}% ${meta.y ?? 0}%` }} />
                  </div>
                  {/* Controls */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace" }}>
                        Photo {i + 1}{i === 0 && photos.length > 1 ? ' · first' : ''}
                      </span>
                      <button onClick={() => { onChange(photos.filter((_, j) => j !== i)); removeMeta(i); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crimson)', fontSize: 11, fontWeight: 700, fontFamily: "'Nunito', sans-serif", padding: 0 }}>
                        Remove
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                      {SLIDERS.map(({ key, label, min, max, step, fmt }) => (
                        <div key={key}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700 }}>{label}</span>
                            <span style={{ fontSize: 10, color: 'var(--crimson)', fontWeight: 800, fontFamily: 'monospace' }}>{fmt(meta[key] ?? (key === 'zoom' ? 1 : key === 'x' ? 50 : 0))}</span>
                          </div>
                          <input type="range" min={min} max={max} step={step}
                            value={meta[key] ?? (key === 'zoom' ? 1 : key === 'x' ? 50 : 0)}
                            onChange={e => updateMeta(i, key, parseFloat(e.target.value))}
                            style={{ width: '100%', accentColor: 'var(--crimson)' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => inputRef.current?.click()}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text)', fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 700, cursor: uploading ? 'wait' : 'pointer' }}>
          <Icon name="upload" size={13} stroke="var(--deep-rose)" />
          {uploading ? 'Uploading...' : 'Upload Photo'}
        </button>
        <button onClick={handleUrl}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text)', fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          <Icon name="link" size={13} stroke="var(--deep-rose)" /> Paste URL
        </button>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--crimson)', marginTop: 4 }}>{error}</div>}
      {photos.length > 1 && <div style={{ fontSize: 10, color: 'var(--deep-sage)', marginTop: 5 }}>✓ Auto-slides every 3.5s · use sliders to crop each photo</div>}
      {photos.length === 0 && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 5 }}>Add multiple photos for an auto-sliding gallery.</div>}
    </div>
  );
}

const DEFAULT_QUICK_FACTS = [
  {key:'Based', value:'Atlanta, Georgia', sub:''},
  {key:'Current Role', value:'Digital Producer · Corporate', sub:'TEGNA / Nexstar'},
  {key:'Path', value:'Local → Streaming → Corporate', sub:''},
  {key:'Target Role', value:'Director of Social · VP Brand', sub:'Head of Content'},
  {key:'Superpower', value:'Short Form Strategy', sub:''},
  {key:'Awards', value:'Murrow · 2× Emmy · NABJ', sub:''},
];
const DEFAULT_PITCH_CARDS = [
  {icon:'mic', color:'rose', title:'I know content from the inside.', desc:"Starting as a local digital producer, I moved into streaming and eventually corporate — so I understand how brand voice has to hold up across every format, platform, and audience."},
  {icon:'gear', color:'gold', title:'I build the machine.', desc:"SOPs, workflow systems, training programs, API automations — I don't just fix problems, I eliminate the conditions that create them. Repeatedly."},
  {icon:'chart', color:'sage', title:'The data backs it up.', desc:"Driving 12.7M views a month, every month, across 60+ stations. #1 on my team in every Instagram and TikTok metric. These aren't estimates. They're the export."},
];

/* ── PROFILE EDITOR ── */
function ProfileEditor({ data, update, setLocalContent }) {
  const links = data?.links || [];

  // Initialize quickFacts and pitchCards with defaults if not yet saved to content.json
  useEffect(() => {
    if (!data?.quickFacts?.length || !data?.pitchCards?.length) {
      setLocalContent(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...(!prev.profile?.quickFacts?.length ? { quickFacts: DEFAULT_QUICK_FACTS } : {}),
          ...(!prev.profile?.pitchCards?.length ? { pitchCards: DEFAULT_PITCH_CARDS } : {}),
        }
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const updateLink = (idx, field, val) => {
    const next = [...links]; next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, links: next } }));
  };
  const addLink = () => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, links: [...(prev.profile.links || []), { id: Date.now().toString(), label: '', url: '' }] } }));
  const delLink = idx => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, links: (prev.profile.links || []).filter((_, i) => i !== idx) } }));

  return (
    <div>
      <SectionHeader title="Profile" sub="// Your name, bio, photo, contact details" />
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Full Name"><Input value={data?.name} onChange={v => update('profile.name', v)} placeholder="Makayla Richards" /></Field>
          <Field label="Title / Role"><Input value={data?.title} onChange={v => update('profile.title', v)} placeholder="Director of Social & Brand Strategy" /></Field>
          <Field label="Subtitle (topbar)" style={{ gridColumn: 'span 2' }}><Input value={data?.subtitle} onChange={v => update('profile.subtitle', v)} /></Field>
          <Field label="Location"><Input value={data?.location} onChange={v => update('profile.location', v)} /></Field>
          <Field label="Status Pill"><Input value={data?.status} onChange={v => update('profile.status', v)} placeholder="Open to Opportunities" /></Field>
          <Field label="Email"><Input value={data?.email} onChange={v => update('profile.email', v)} type="email" /></Field>
          <Field label="Phone"><Input value={data?.phone} onChange={v => update('profile.phone', v)} /></Field>
          <Field label="LinkedIn URL"><Input value={data?.linkedin} onChange={v => update('profile.linkedin', v)} placeholder="https://linkedin.com/in/..." /></Field>
          <Field label="Resume URL"><Input value={data?.resumeUrl} onChange={v => update('profile.resumeUrl', v)} placeholder="Link to resume PDF" /></Field>
        </div>
        <Field label="Hero Tagline — Line 1" hint="Shown in white bold italic on the Overview hero"><Input value={data?.tagline} onChange={v => update('profile.tagline', v)} placeholder="Not just a producer." /></Field>
        <Field label="Hero Tagline — Line 2 (accent)" hint="Shown in rose below the tagline"><Input value={data?.taglineAccent} onChange={v => update('profile.taglineAccent', v)} placeholder="An operator." /></Field>
        <Field label="Short Bio (overview page)"><Textarea value={data?.bio} onChange={v => update('profile.bio', v)} rows={3} /></Field>
        <Field label="About Bio (about page)"><Textarea value={data?.aboutBio} onChange={v => update('profile.aboutBio', v)} rows={4} /></Field>
        <Field label="AI Agent Label" hint="Small line above the chat bar — e.g. 'Connect.' or 'Not sure I'm the right fit? Ask.'"><Input value={data?.agentLabel} onChange={v => update('profile.agentLabel', v)} placeholder="Connect." /></Field>
        <Field label="Pitch Line"><Input value={data?.pitch} onChange={v => update('profile.pitch', v)} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Timer — top line" hint="The 'hire me' counter label"><Input value={data?.timerLabel} onChange={v => update('profile.timerLabel', v)} placeholder="You haven't hired me for" /></Field>
          <Field label="Timer — bottom line"><Input value={data?.timerSubtext} onChange={v => update('profile.timerSubtext', v)} placeholder="What are you waiting for?" /></Field>
        </div>
        <div style={{ border: '1.5px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 20, background: 'var(--warm)' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>Photo Slots</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 16, fontFamily: "'Courier Prime', monospace" }}>// Each slot is independent — upload different photos to each</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>Sidebar Avatar (single photo)</div>
              <ImageUploader value={data?.photo} onChange={v => update('profile.photo', v)} label="Avatar" />
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Overview Hero Gallery</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 10 }}>Appears in the large hero panel on the Overview page. Add multiple for a slideshow.</div>
              <PhotoGalleryEditor
                photos={Array.isArray(data?.photos) ? data.photos : (data?.photo ? [data.photo] : [])}
                onChange={photos => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, photos } }))}
                photoMeta={data?.photoMeta || []}
                onChangeMeta={photoMeta => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, photoMeta } }))}
              />
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>About Page Gallery</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 10 }}>Appears in the photo panel on the About page. Add multiple for a slideshow.</div>
              <PhotoGalleryEditor
                photos={Array.isArray(data?.aboutPhotos) ? data.aboutPhotos : (data?.photo ? [data.photo] : [])}
                onChange={photos => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, aboutPhotos: photos } }))}
                photoMeta={data?.aboutPhotoMeta || []}
                onChangeMeta={aboutPhotoMeta => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, aboutPhotoMeta } }))}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Facts */}
      <div style={{ marginTop: 8, marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>Quick Facts <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>— the table on the About page</span></div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12, fontFamily: "'Courier Prime', monospace" }}>// Each row is a label + value pair. Sub is optional (shows smaller below the value).</div>
        {(data?.quickFacts || []).map((f, i) => (
          <Card key={i}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 1.5fr auto', gap: 12, alignItems: 'end' }}>
              <Field label="Label"><Input value={f.key} onChange={v => { const next = [...(data.quickFacts||[])]; next[i]={...next[i],key:v}; setLocalContent(prev=>({...prev,profile:{...prev.profile,quickFacts:next}})); }} placeholder="Based" /></Field>
              <Field label="Value"><Input value={f.value} onChange={v => { const next = [...(data.quickFacts||[])]; next[i]={...next[i],value:v}; setLocalContent(prev=>({...prev,profile:{...prev.profile,quickFacts:next}})); }} placeholder="Atlanta, Georgia" /></Field>
              <Field label="Sub (optional)"><Input value={f.sub||''} onChange={v => { const next = [...(data.quickFacts||[])]; next[i]={...next[i],sub:v}; setLocalContent(prev=>({...prev,profile:{...prev.profile,quickFacts:next}})); }} placeholder="e.g. TEGNA / Nexstar" /></Field>
              <div style={{ paddingBottom: 4 }}><DeleteBtn onClick={() => setLocalContent(prev=>({...prev,profile:{...prev.profile,quickFacts:(prev.profile.quickFacts||[]).filter((_,j)=>j!==i)}}))} /></div>
            </div>
          </Card>
        ))}
        <AddBtn onClick={() => setLocalContent(prev=>({...prev,profile:{...prev.profile,quickFacts:[...(prev.profile.quickFacts||[]),{key:'',value:'',sub:''}]}}))} label="Add Fact" />
      </div>

      {/* Pitch Cards */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>The Pitch Cards <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>— three cards on the About page</span></div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12, fontFamily: "'Courier Prime', monospace" }}>// Icon options: mic, gear, chart, bulb, star, lightning, camera, people</div>
        {(data?.pitchCards || []).map((c, i) => (
          <Card key={i}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}><DeleteBtn onClick={() => setLocalContent(prev=>({...prev,profile:{...prev.profile,pitchCards:(prev.profile.pitchCards||[]).filter((_,j)=>j!==i)}}))} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <Field label="Icon"><Input value={c.icon||''} onChange={v => { const next=[...(data.pitchCards||[])]; next[i]={...next[i],icon:v}; setLocalContent(prev=>({...prev,profile:{...prev.profile,pitchCards:next}})); }} placeholder="mic" /></Field>
              <Field label="Color">
                <select value={c.color||'rose'} onChange={e => { const next=[...(data.pitchCards||[])]; next[i]={...next[i],color:e.target.value}; setLocalContent(prev=>({...prev,profile:{...prev.profile,pitchCards:next}})); }} style={{width:'100%',padding:'10px 14px',borderRadius:10,border:'1.5px solid var(--border2)',background:'white',fontFamily:"'Nunito',sans-serif",fontSize:13}}>
                  {['rose','sage','gold','periwinkle','ink'].map(col=><option key={col} value={col}>{col}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Title"><Input value={c.title||''} onChange={v => { const next=[...(data.pitchCards||[])]; next[i]={...next[i],title:v}; setLocalContent(prev=>({...prev,profile:{...prev.profile,pitchCards:next}})); }} placeholder="I know content from the inside." /></Field>
            <Field label="Description"><Textarea value={c.desc||''} rows={3} onChange={v => { const next=[...(data.pitchCards||[])]; next[i]={...next[i],desc:v}; setLocalContent(prev=>({...prev,profile:{...prev.profile,pitchCards:next}})); }} placeholder="Describe this aspect of your work..." /></Field>
          </Card>
        ))}
        <AddBtn onClick={() => setLocalContent(prev=>({...prev,profile:{...prev.profile,pitchCards:[...(prev.profile.pitchCards||[]),{icon:'bulb',color:'rose',title:'',desc:''}]}}))} label="Add Card" />
      </div>

      {/* Hero Tags */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>Hero Skill Tags <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>— the chips shown in the hero bio section on the Overview page</span></div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontFamily: "'Courier Prime', monospace" }}>// One tag per line</div>
        <Textarea
          value={Array.isArray(data?.heroTags) ? data.heroTags.join('\n') : (data?.heroTags || '')}
          onChange={v => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, heroTags: v.split('\n').map(t => t.trim()).filter(Boolean) } }))}
          placeholder={"Short Form Strategy\nBrand Voice\nSocial Operations"}
          rows={6}
        />
      </div>

      {/* Also Known For */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>Also Known For <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>— chips on the Awards page</span></div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontFamily: "'Courier Prime', monospace" }}>// One item per line</div>
        <Textarea
          value={Array.isArray(data?.alsoKnownFor) ? data.alsoKnownFor.join('\n') : (data?.alsoKnownFor || '')}
          onChange={v => setLocalContent(prev => ({ ...prev, profile: { ...prev.profile, alsoKnownFor: v.split('\n').map(t => t.trim()).filter(Boolean) } }))}
          placeholder={"Staff Trainer\nOnboarding Lead\nMentorship"}
          rows={4}
        />
      </div>

      {/* What's Next */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>What's Next Text <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>— paragraph on the Timeline page</span></div>
        <Textarea value={data?.whatNextText} onChange={v => update('profile.whatNextText', v)} rows={3} placeholder="Ready for a seat at the table..." />
      </div>

      {/* Additional Links */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 12 }}>Additional Links <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>— appear in the sidebar under Resume</span></div>
        {links.map((l, i) => (
          <Card key={l.id}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 12, alignItems: 'end' }}>
              <Field label="Button Label"><Input value={l.label} onChange={v => updateLink(i, 'label', v)} placeholder="My Website" /></Field>
              <Field label="URL"><Input value={l.url} onChange={v => updateLink(i, 'url', v)} placeholder="https://..." /></Field>
              <div style={{ paddingBottom: 4 }}><DeleteBtn onClick={() => delLink(i)} /></div>
            </div>
          </Card>
        ))}
        <AddBtn onClick={addLink} label="Add Link" />
      </div>
    </div>
  );
}

/* ── OVERVIEW EDITOR ── */
const LOG_COLOR_OPTIONS = [
  { value: 'deep-sage', label: 'Green (deep-sage)' },
  { value: 'periwinkle', label: 'Purple (periwinkle)' },
  { value: 'crimson', label: 'Red (crimson)' },
  { value: 'deep-rose', label: 'Rose (deep-rose)' },
  { value: 'deep-gold', label: 'Gold (deep-gold)' },
  { value: 'sage', label: 'Sage (sage)' },
  { value: 'rose', label: 'Pink (rose)' },
  { value: 'gold', label: 'Yellow (gold)' },
  { value: 'ink', label: 'Dark (ink)' },
];

function OverviewEditor({ data, setLocalContent }) {
  const log = data?.activityLog || [];

  const updateEntry = (idx, field, val) => {
    const next = [...log]; next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, activityLog: next }));
  };
  const addEntry = () => setLocalContent(prev => ({
    ...prev,
    activityLog: [...(prev.activityLog || []), { time: '', color: 'deep-sage', text: '' }],
  }));
  const delEntry = idx => setLocalContent(prev => ({
    ...prev,
    activityLog: (prev.activityLog || []).filter((_, i) => i !== idx),
  }));

  return (
    <div>
      <SectionHeader title="Overview" sub="// Activity log shown on the homepage" />
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>Activity Log <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>— the live feed on the right side of the homepage</span></div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12, fontFamily: "'Courier Prime', monospace" }}>// "Time" is the label on the left (e.g. NOW, 2026, 2025). Drag entries to reorder (save to apply).</div>
      {log.map((entry, i) => (
        <Card key={i}>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 160px auto', gap: 12, alignItems: 'end' }}>
            <Field label="Time">
              <Input value={entry.time} onChange={v => updateEntry(i, 'time', v)} placeholder="NOW" />
            </Field>
            <Field label="Text">
              <Input value={entry.text} onChange={v => updateEntry(i, 'text', v)} placeholder="Activity description..." />
            </Field>
            <Field label="Color">
              <select value={entry.color} onChange={e => updateEntry(i, 'color', e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 }}>
                {LOG_COLOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <div style={{ paddingBottom: 4 }}><DeleteBtn onClick={() => delEntry(i)} /></div>
          </div>
        </Card>
      ))}
      <AddBtn onClick={addEntry} label="Add Log Entry" />
    </div>
  );
}

/* ── STATS & PILLS EDITOR ── */
function StatsEditor({ data, update, setLocalContent }) {
  const stats = data?.stats || [];
  const pills = data?.pills || [];

  const updateStat = (idx, field, val) => {
    const next = [...stats];
    next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, stats: next }));
  };
  const updatePill = (idx, field, val) => {
    const next = [...pills];
    next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, pills: next }));
  };
  const addStat = () => setLocalContent(prev => ({ ...prev, stats: [...(prev.stats || []), { id: Date.now().toString(), label: '', value: '', sub: '', icon: 'chart', color: 'rose' }] }));
  const delStat = idx => setLocalContent(prev => ({ ...prev, stats: prev.stats.filter((_, i) => i !== idx) }));
  const addPill = () => setLocalContent(prev => ({ ...prev, pills: [...(prev.pills || []), { id: Date.now().toString(), text: '', color: 'rose' }] }));
  const delPill = idx => setLocalContent(prev => ({ ...prev, pills: prev.pills.filter((_, i) => i !== idx) }));

  return (
    <div>
      <SectionHeader title="Stats & Pills" sub="// The 5 number cards + the badge pills at the top of your homepage" />
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 12 }}>Stat Cards</div>
        {stats.map((s, i) => (
          <Card key={s.id}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}><DeleteBtn onClick={() => delStat(i)} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <Field label="Value"><Input value={s.value} onChange={v => updateStat(i, 'value', v)} placeholder="152M" /></Field>
              <Field label="Label"><Input value={s.label} onChange={v => updateStat(i, 'label', v)} placeholder="Reel Plays / Day" /></Field>
              <Field label="Sub text"><Input value={s.sub} onChange={v => updateStat(i, 'sub', v)} placeholder="12 months, all platforms" /></Field>
              <Field label="Color">
                <select value={s.color} onChange={e => updateStat(i, 'color', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 }}>
                  {['rose','sage','gold','periwinkle','ink'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
          </Card>
        ))}
        <AddBtn onClick={addStat} label="Add Stat Card" />
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 12 }}>Badge Pills</div>
        {pills.map((p, i) => (
          <Card key={p.id}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'center' }}>
              <Field label="Text"><Input value={p.text} onChange={v => updatePill(i, 'text', v)} placeholder="Murrow Award Winner" /></Field>
              <Field label="Color">
                <select value={p.color} onChange={e => updatePill(i, 'color', e.target.value)} style={{ padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 }}>
                  {['rose','sage','gold','periwinkle'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <div style={{ paddingTop: 18 }}><DeleteBtn onClick={() => delPill(i)} /></div>
            </div>
          </Card>
        ))}
        <AddBtn onClick={addPill} label="Add Badge Pill" />
      </div>
    </div>
  );
}

/* ── PORTFOLIO EDITOR ── */
function PortfolioEditor({ data, setLocalContent }) {
  const posts = data || [];

  const update = (idx, field, val) => {
    const next = [...posts];
    next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, portfolio: next }));
  };
  const updateStat = (idx, si, field, val) => {
    const next = [...posts];
    const stats = [...(next[idx].stats || [])];
    stats[si] = { ...stats[si], [field]: val };
    next[idx] = { ...next[idx], stats };
    setLocalContent(prev => ({ ...prev, portfolio: next }));
  };
  const addPost = () => setLocalContent(prev => ({ ...prev, portfolio: [...(prev.portfolio || []), { id: Date.now().toString(), title: '', desc: '', category: 'reels', platform: '', date: '', mediaUrl: '', embedCode: '', thumbnail: '', link: '', stats: [{ label: '', value: '' }], badge: '' }] }));
  const delPost = idx => setLocalContent(prev => ({ ...prev, portfolio: prev.portfolio.filter((_, i) => i !== idx) }));

  return (
    <div>
      <SectionHeader title="Portfolio" sub={`// The cards on your Portfolio page (${posts.length} items). Click a row to expand it.`} />
      {posts.map((post, i) => (
        <CollapsibleCard key={post.id} title={post.title || `Post ${i + 1}`} meta={post.category} onDelete={() => delPost(i)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Title" style={{ gridColumn: 'span 2' }}><Input value={post.title} onChange={v => update(i, 'title', v)} /></Field>
            <Field label="Description" style={{ gridColumn: 'span 2' }}><Textarea value={post.desc} onChange={v => update(i, 'desc', v)} rows={3} /></Field>
            <Field label="Category">
              <select value={post.category} onChange={e => update(i, 'category', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 }}>
                <option value="reels">Social Reels</option>
                <option value="oncamera">On-Camera / VO Work</option>
                <option value="longform">Long-Form Videos</option>
                <option value="systems">Systems</option>
                <option value="consulting">Brand / Digital Consulting</option>
                <option value="design">Brand & Design</option>
                <option value="writing">Writing</option>
              </select>
            </Field>
            <Field label="Platform / Publication"><Input value={post.platform} onChange={v => update(i, 'platform', v)} placeholder="Instagram · 11Alive" /></Field>
            <Field label="Date"><Input value={post.date} onChange={v => update(i, 'date', v)} placeholder="Apr 7, 2026" /></Field>
            <Field label="Badge Label"><Input value={post.badge} onChange={v => update(i, 'badge', v)} placeholder="Top Performer" /></Field>
          </div>

          {/* Media section */}
          <div style={{ marginTop: 4, padding: 16, background: 'var(--warm)', borderRadius: 12, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Media</div>
            <Field label="Media URL" hint="Paste any URL — Instagram, Facebook, TikTok, YouTube, Vimeo. Auto-detected and embedded.">
              <Input value={post.mediaUrl} onChange={v => update(i, 'mediaUrl', v)} placeholder="https://www.instagram.com/reel/..." />
            </Field>
            <Field label="External Link" hint="For Writing/Consulting entries — article URL, case study link, etc. Shows an ↗ Open button.">
              <Input value={post.link || ''} onChange={v => update(i, 'link', v)} placeholder="https://..." />
            </Field>
            <Field label="Raw Embed Code" hint="Optional: paste raw HTML embed code from Instagram/FB/TikTok Share → Embed. Overrides Media URL if provided.">
              <Textarea value={post.embedCode} onChange={v => update(i, 'embedCode', v)} placeholder='<blockquote class="instagram-media"...' rows={3} />
            </Field>
            <Field label="Thumbnail / Cover Image" hint="Shown when no embed is available, or as a cover for Writing/Systems/Consulting entries.">
              <ImageUploader value={post.thumbnail} onChange={v => update(i, 'thumbnail', v)} label="Thumbnail" />
            </Field>
          </div>

          {/* Stats */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>Stats</div>
            {(post.stats || []).map((s, si) => (
              <div key={si} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <Input value={s.value} onChange={v => updateStat(i, si, 'value', v)} placeholder="542K" />
                <Input value={s.label} onChange={v => updateStat(i, si, 'label', v)} placeholder="Plays" />
                <DeleteBtn onClick={() => {
                  const next = [...posts];
                  next[i] = { ...next[i], stats: next[i].stats.filter((_, sii) => sii !== si) };
                  setLocalContent(prev => ({ ...prev, portfolio: next }));
                }} />
              </div>
            ))}
            <button onClick={() => {
              const next = [...posts];
              next[i] = { ...next[i], stats: [...(next[i].stats || []), { label: '', value: '' }] };
              setLocalContent(prev => ({ ...prev, portfolio: next }));
            }} style={{ fontSize: 11, color: 'var(--deep-rose)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 }}>+ Add stat</button>
          </div>
        </CollapsibleCard>
      ))}
      <AddBtn onClick={addPost} label="Add Portfolio Item" />
    </div>
  );
}

/* ── SKILLS EDITOR ── */
function SkillsEditor({ data, setLocalContent }) {
  const skills = data || [];
  const update = (idx, field, val) => {
    const next = [...skills]; next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, skills: next }));
  };
  const addSkill = () => setLocalContent(prev => ({ ...prev, skills: [...(prev.skills || []), { id: Date.now().toString(), name: '', icon: 'bulb', color: 'rose', desc: '', tags: [] }] }));
  const delSkill = idx => setLocalContent(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== idx) }));

  return (
    <div>
      <SectionHeader title="Skills" sub="// The capability cards on your Skills page" />
      {skills.map((s, i) => (
        <CollapsibleCard key={s.id} title={s.name || `Skill ${i + 1}`} accent={s.color} onDelete={() => delSkill(i)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Name"><Input value={s.name} onChange={v => update(i, 'name', v)} /></Field>
            <Field label="Color">
              <select value={s.color} onChange={e => update(i, 'color', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 }}>
                {['rose','sage','gold','periwinkle','ink'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description" style={{ gridColumn: 'span 2' }}><Textarea value={s.desc} onChange={v => update(i, 'desc', v)} rows={3} /></Field>
            <Field label="Tags (comma-separated)" style={{ gridColumn: 'span 2' }}>
              <Input value={Array.isArray(s.tags) ? s.tags.join(', ') : s.tags} onChange={v => update(i, 'tags', v.split(',').map(t => t.trim()).filter(Boolean))} placeholder="Reels, TikTok, Vertical Video" />
            </Field>
          </div>
        </CollapsibleCard>
      ))}
      <AddBtn onClick={addSkill} label="Add Skill" />
    </div>
  );
}

/* ── SYSTEMS EDITOR ── */
function SystemsEditor({ data, setLocalContent }) {
  const systems = data || [];
  const update = (idx, field, val) => {
    const next = [...systems]; next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, systems: next }));
  };
  const addSystem = () => setLocalContent(prev => ({ ...prev, systems: [...(prev.systems || []), { id: Date.now().toString(), title: '', tag: '', color: 'rose', desc: '', metrics: [{ value: '', sub: '' }, { value: '', sub: '' }] }] }));
  const delSystem = idx => setLocalContent(prev => ({ ...prev, systems: prev.systems.filter((_, i) => i !== idx) }));

  return (
    <div>
      <SectionHeader title="Systems Built" sub="// The case studies on your Systems Built page" />
      {systems.map((s, i) => (
        <CollapsibleCard key={s.id} title={s.title || `System ${i + 1}`} accent={s.color} meta={s.tag} onDelete={() => delSystem(i)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Title" style={{ gridColumn: 'span 2' }}><Input value={s.title} onChange={v => update(i, 'title', v)} /></Field>
            <Field label="Tag"><Input value={s.tag} onChange={v => update(i, 'tag', v)} placeholder="Training" /></Field>
            <Field label="Color">
              <select value={s.color} onChange={e => update(i, 'color', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 }}>
                {['rose','sage','gold','periwinkle','ink'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description" style={{ gridColumn: 'span 2' }}><Textarea value={s.desc} onChange={v => update(i, 'desc', v)} rows={3} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 4 }}>
            {(s.metrics || []).map((m, mi) => (
              <div key={mi} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Field label={`Metric ${mi + 1} Value`}><Input value={m.value} onChange={v => { const next = [...systems]; next[i].metrics[mi] = { ...next[i].metrics[mi], value: v }; setLocalContent(prev => ({ ...prev, systems: next })); }} /></Field>
                <Field label="Sub"><Input value={m.sub} onChange={v => { const next = [...systems]; next[i].metrics[mi] = { ...next[i].metrics[mi], sub: v }; setLocalContent(prev => ({ ...prev, systems: next })); }} /></Field>
              </div>
            ))}
          </div>
        </CollapsibleCard>
      ))}
      <AddBtn onClick={addSystem} label="Add Case Study" />
    </div>
  );
}

/* ── AWARDS EDITOR ── */
function AwardsEditor({ data, setLocalContent }) {
  const awards = data || [];
  const update = (idx, field, val) => {
    const next = [...awards]; next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, awards: next }));
  };
  const addAward = () => setLocalContent(prev => ({ ...prev, awards: [...(prev.awards || []), { id: Date.now().toString(), name: '', org: '', icon: 'trophy' }] }));
  const delAward = idx => setLocalContent(prev => ({ ...prev, awards: prev.awards.filter((_, i) => i !== idx) }));

  return (
    <div>
      <SectionHeader title="Awards & Recognition" sub="// The awards listed on your Recognition page" />
      {awards.map((a, i) => (
        <Card key={a.id}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <Field label="Award Name"><Input value={a.name} onChange={v => update(i, 'name', v)} /></Field>
            <Field label="Organization / Context"><Input value={a.org} onChange={v => update(i, 'org', v)} /></Field>
            <div style={{ paddingBottom: 4 }}><DeleteBtn onClick={() => delAward(i)} /></div>
          </div>
        </Card>
      ))}
      <AddBtn onClick={addAward} label="Add Award" />
    </div>
  );
}

/* ── TIMELINE EDITOR ── */
function TimelineEditor({ data, setLocalContent }) {
  const tl = data || [];
  const update = (idx, field, val) => {
    const next = [...tl]; next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, timeline: next }));
  };
  const addEntry = () => setLocalContent(prev => ({ ...prev, timeline: [...(prev.timeline || []), { id: Date.now().toString(), year: '', title: '', sub: '', color: '#c4687a' }] }));
  const delEntry = idx => setLocalContent(prev => ({ ...prev, timeline: prev.timeline.filter((_, i) => i !== idx) }));

  return (
    <div>
      <SectionHeader title="Career Timeline" sub="// The entries on your Career Timeline page" />
      {tl.map((t, i) => (
        <CollapsibleCard key={t.id} title={t.title || `Entry ${i + 1}`} meta={t.year} onDelete={() => delEntry(i)}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
            <Field label="Year / Label"><Input value={t.year} onChange={v => update(i, 'year', v)} placeholder="2025" /></Field>
            <Field label="Title"><Input value={t.title} onChange={v => update(i, 'title', v)} /></Field>
            <Field label="Description" style={{ gridColumn: 'span 2' }}><Textarea value={t.sub} onChange={v => update(i, 'sub', v)} rows={2} /></Field>
          </div>
        </CollapsibleCard>
      ))}
      <AddBtn onClick={addEntry} label="Add Timeline Entry" />
    </div>
  );
}

/* ── AGENT LOG ── */
function AgentLogEditor({ pw }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [clearing, setClearing] = useState(false);
  const [savingId, setSavingId] = useState('');

  const call = async (body) => {
    const res = await fetch('/.netlify/functions/agent-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: pw, ...body }),
    });
    return res.json();
  };

  const fetchLog = async () => {
    setLoading(true); setErr('');
    const data = await call({ action: 'get' }).catch(() => ({ error: 'Network error' }));
    setLoading(false);
    if (data.error) { setErr(data.error); return; }
    setEntries(data.entries || []);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLog(); }, []);

  const saveGrade = async (id, grade, feedback) => {
    setSavingId(id);
    await call({ action: 'grade', id, grade, feedback }).catch(() => {});
    setSavingId('');
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all agent log entries? This cannot be undone.')) return;
    setClearing(true);
    await call({ action: 'clear' }).catch(() => {});
    setClearing(false);
    setEntries([]);
  };

  const exportData = async (format) => {
    const data = await call({ action: 'export', format }).catch(() => null);
    if (!data) return;
    const blob = new Blob(
      [format === 'csv' ? data : JSON.stringify(data.entries, null, 2)],
      { type: format === 'csv' ? 'text/csv' : 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `agent-log.${format}`; a.click();
    URL.revokeObjectURL(url);
  };

  const updateEntry = (id, field, val) =>
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: val } : e));

  const fmtDate = ts => {
    try { return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  const gradeColor = g => g >= 4 ? 'var(--deep-sage)' : g >= 3 ? 'var(--deep-gold)' : g ? 'var(--crimson)' : 'var(--text3)';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, fontStyle: 'italic', color: 'var(--ink)', marginBottom: 4 }}>Agent Log</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace" }}>// Last 30 days of visitor Q&amp;A — grade to train your intuition</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={() => exportData('json')} style={{ padding: '8px 14px', borderRadius: 9, border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text)', fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Export JSON</button>
          <button onClick={() => exportData('csv')} style={{ padding: '8px 14px', borderRadius: 9, border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text)', fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Export CSV</button>
          <button onClick={clearAll} disabled={clearing} style={{ padding: '8px 14px', borderRadius: 9, border: 'none', background: 'var(--crimson)', color: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 700, cursor: clearing ? 'wait' : 'pointer' }}>{clearing ? 'Clearing…' : 'Clear All'}</button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 14, fontStyle: 'italic' }}>Loading log…</div>}
      {err && <div style={{ padding: 16, background: 'var(--blush)', borderRadius: 12, color: 'var(--crimson)', fontSize: 13 }}>{err} {err.includes('function') && <span style={{ color: 'var(--text3)' }}>(only works on the live Netlify site)</span>}</div>}
      {!loading && !err && entries && entries.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)', fontSize: 14 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontStyle: 'italic', marginBottom: 6 }}>No entries yet</div>
          Visitor Q&amp;A will appear here once your agent gets some questions.
        </div>
      )}
      {!loading && !err && (entries || []).map(entry => (
        <div key={entry.id} style={{ background: 'white', borderRadius: 14, border: '1.5px solid var(--border)', padding: 20, marginBottom: 14, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, color: 'var(--text3)', letterSpacing: '.06em' }}>{fmtDate(entry.timestamp)}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button key={star} onClick={() => {
                  updateEntry(entry.id, 'grade', star);
                  saveGrade(entry.id, star, entry.feedback || '');
                }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 1px', color: (entry.grade || 0) >= star ? gradeColor(entry.grade) : 'var(--border2)', transition: 'color .15s' }}>★</button>
              ))}
              {savingId === entry.id && <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace", alignSelf: 'center', marginLeft: 4 }}>saving…</span>}
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 6, lineHeight: 1.4 }}>Q: {entry.question}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 10, paddingLeft: 12, borderLeft: '3px solid var(--border2)' }}>{entry.answer}</div>
          <textarea
            value={entry.feedback || ''}
            onChange={e => updateEntry(entry.id, 'feedback', e.target.value)}
            onBlur={e => saveGrade(entry.id, entry.grade, e.target.value)}
            placeholder="Add feedback or notes about this response…"
            rows={2}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 9, border: '1.5px solid var(--border2)', background: 'var(--warm)', fontFamily: "'Nunito', sans-serif", fontSize: 12, color: 'var(--text)', outline: 'none', resize: 'none', transition: 'border .15s' }}
            onFocus={e => e.target.style.borderColor = 'var(--deep-rose)'}
          />
        </div>
      ))}
    </div>
  );
}

/* ── METRICS EDITOR ── */
function ServicesEditor({ data, setLocalContent }) {
  const services = data || { tracks: [] };
  const setSvc = updater => setLocalContent(prev => ({ ...prev, services: updater(prev.services || { tracks: [] }) }));
  const setTop = (field, val) => setSvc(s => ({ ...s, [field]: val }));
  const setTrack = (ti, field, val) => setSvc(s => { const tracks = [...(s.tracks || [])]; tracks[ti] = { ...tracks[ti], [field]: val }; return { ...s, tracks }; });
  const setItem = (ti, ii, field, val) => setSvc(s => { const tracks = [...(s.tracks || [])]; const items = [...(tracks[ti].items || [])]; items[ii] = { ...items[ii], [field]: val }; tracks[ti] = { ...tracks[ti], items }; return { ...s, tracks }; });
  const addItem = ti => setSvc(s => { const tracks = [...(s.tracks || [])]; const items = [...(tracks[ti].items || []), { title: '', tag: '', color: 'rose', desc: '' }]; tracks[ti] = { ...tracks[ti], items }; return { ...s, tracks }; });
  const delItem = (ti, ii) => setSvc(s => { const tracks = [...(s.tracks || [])]; const items = (tracks[ti].items || []).filter((_, i) => i !== ii); tracks[ti] = { ...tracks[ti], items }; return { ...s, tracks }; });
  const selStyle = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 };

  return (
    <div>
      <SectionHeader title="Services" sub="// The 'What I Do' block on your homepage" />
      <Card>
        <Field label="Section Label"><Input value={services.label} onChange={v => setTop('label', v)} placeholder="What I Do" /></Field>
        <Field label="Intro line"><Textarea value={services.intro} onChange={v => setTop('intro', v)} rows={2} placeholder="Whether you're looking to hire or looking for a strategic partner..." /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="CTA text"><Input value={services.ctaLabel} onChange={v => setTop('ctaLabel', v)} placeholder="Ready to work together?" /></Field>
          <Field label="CTA button"><Input value={services.ctaButton} onChange={v => setTop('ctaButton', v)} placeholder="Let's Talk" /></Field>
        </div>
      </Card>
      {(services.tracks || []).map((track, ti) => (
        <Card key={track.id || ti}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Track heading"><Input value={track.heading} onChange={v => setTrack(ti, 'heading', v)} placeholder="For Brands & Businesses" /></Field>
            <Field label="Track subhead"><Input value={track.sub} onChange={v => setTrack(ti, 'sub', v)} placeholder="Consulting through Village Values" /></Field>
          </div>
          {(track.items || []).map((it, ii) => (
            <div key={ii} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
                <Field label="Title"><Input value={it.title} onChange={v => setItem(ti, ii, 'title', v)} placeholder="Content Strategy & Social Systems" /></Field>
                <Field label="Tag"><Input value={it.tag} onChange={v => setItem(ti, ii, 'tag', v)} placeholder="Most Requested" /></Field>
                <Field label="Color">
                  <select value={it.color} onChange={e => setItem(ti, ii, 'color', e.target.value)} style={selStyle}>
                    {['rose', 'sage', 'gold', 'periwinkle', 'ink'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <div style={{ paddingBottom: 4 }}><DeleteBtn onClick={() => delItem(ti, ii)} /></div>
              </div>
              <Field label="Description"><Textarea value={it.desc} onChange={v => setItem(ti, ii, 'desc', v)} rows={2} placeholder="Audit your current content operation..." /></Field>
            </div>
          ))}
          <AddBtn onClick={() => addItem(ti)} label="Add Service" />
        </Card>
      ))}
    </div>
  );
}

function MetricsEditor({ data, setLocalContent }) {
  const metrics = data || [];
  const update = (idx, field, val) => {
    const next = [...metrics]; next[idx] = { ...next[idx], [field]: val };
    setLocalContent(prev => ({ ...prev, metrics: next }));
  };
  const addMetric = () => setLocalContent(prev => ({ ...prev, metrics: [...(prev.metrics || []), { id: Date.now().toString(), label: '', value: '', sub: '', color: 'rose' }] }));
  const delMetric = idx => setLocalContent(prev => ({ ...prev, metrics: prev.metrics.filter((_, i) => i !== idx) }));

  return (
    <div>
      <SectionHeader title="Metrics" sub="// The performance numbers on your metrics page" />
      {metrics.map((m, i) => (
        <Card key={m.id}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <Field label="Value"><Input value={m.value} onChange={v => update(i, 'value', v)} placeholder="31.9M" /></Field>
            <Field label="Label"><Input value={m.label} onChange={v => update(i, 'label', v)} placeholder="IG Reel Plays" /></Field>
            <Field label="Sub text"><Input value={m.sub} onChange={v => update(i, 'sub', v)} placeholder="avg 16,264/reel" /></Field>
            <Field label="Color">
              <select value={m.color} onChange={e => update(i, 'color', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border2)', background: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 13 }}>
                {['rose','sage','gold','periwinkle'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <div style={{ paddingBottom: 4 }}><DeleteBtn onClick={() => delMetric(i)} /></div>
          </div>
        </Card>
      ))}
      <AddBtn onClick={addMetric} label="Add Metric" />
    </div>
  );
}

/* ── AGENT SETTINGS ── */
function AgentSettingsEditor({ data, update }) {
  const profile = data?.agentProfile || '';
  const charCount = profile.length;

  return (
    <div>
      <SectionHeader title="Agent Settings" sub="// Edit what your AI assistant knows. Save & Deploy to go live (~30 sec)." />

      <div className="panel" style={{ padding: '22px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--deep-sage)', animation: 'pulse 2s infinite' }} />
          <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, color: 'var(--deep-sage)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Live Agent Profile</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 16 }}>
          This is the system prompt your AI assistant uses when answering questions from visitors — recruiters, clients, and hiring managers. Write it in plain English. Be specific: include your actual metrics, target roles, consulting industries, and what you're open to. The assistant will only say what you put here.
        </p>
        <textarea
          value={profile}
          onChange={e => update('agentProfile', e.target.value)}
          rows={22}
          style={{ width: '100%', fontFamily: "'Courier Prime', monospace", fontSize: 12.5, lineHeight: 1.7, padding: '14px 16px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--warm)', color: 'var(--ink)', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
          placeholder="Describe who Makayla is, what she's done, and what she's open to..."
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace" }}>{charCount.toLocaleString()} characters</span>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>Changes deploy automatically with Save & Deploy</span>
        </div>
      </div>

      <div className="panel" style={{ padding: '18px 24px', background: 'rgba(var(--deep-gold-rgb, 180,140,60),.06)', borderLeft: '3px solid var(--deep-gold)' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--deep-gold)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Tips for a great assistant</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
          Start with tone instructions ("Be warm, concise, third person"). Then add a clearly labeled facts section with bullet points — roles, metrics, skills, industries you consult for, awards, and what you're open to. The more specific you are, the better it answers. If you add client industries or new stats, add them here too so the assistant stays current.
        </div>
      </div>
    </div>
  );
}
